import React, { useState, useEffect, useCallback } from "react";
import {
  useMessagePatterns,
  MessagePattern,
  PatternDelayMode,
} from "../../hooks/useMessagePatterns";
import { supabase } from "../../lib/supabase";
import { PatternSampleModal } from "./PatternSampleModal";

// =============================================================================
// PATTERN REVIEW SECTION
// =============================================================================
// PURPOSE: Admin tool for classifying repeated message patterns as ManyChat (bot)
// or Human Script. Classifications improve DM analytics accuracy by filtering
// out automation from real setter activity.
//
// FEATURES:
// - Tab counts show patterns per classification
// - Full message preview on row expansion
// - Keyboard shortcuts: B = Bot, H = Human, U = Undo
// - Bulk "Mark all as Bot" for efficiency
// - Clear explanations of what each classification means
// =============================================================================

interface PatternReviewSectionProps {
  organizationId: string;
  timezone: string;
  onConversationClick?: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null
  ) => void;
}

interface TabCounts {
  all: number;
  unclassified: number;
  manychat: number;
  human_script: number;
}

// Format delay minutes into readable string (e.g., 30 -> "30min", 90 -> "1hr 30min")
function formatDelayMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}hr`;
  }
  return `${hours}hr ${mins}min`;
}

export const PatternReviewSection: React.FC<PatternReviewSectionProps> = ({
  organizationId,
  timezone,
  onConversationClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState<keyof TabCounts>("unclassified");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    all: 0,
    unclassified: 0,
    manychat: 0,
    human_script: 0,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Modal state for viewing pattern samples in 3-column grid
  const [sampleModalPattern, setSampleModalPattern] = useState<MessagePattern | null>(null);
  // Delay mode cache (keyed by pattern ID)
  const [delayModes, setDelayModes] = useState<Record<string, PatternDelayMode | null>>({});
  const [loadingDelayMode, setLoadingDelayMode] = useState<string | null>(null);

  const {
    patterns,
    isLoading,
    isRefreshing,
    error,
    fetchPatterns,
    refreshPatterns,
    classifyPattern,
    fetchDelayMode,
  } = useMessagePatterns({ organizationId, classificationFilter: filter });

  // Fetch tab counts separately (always shows accurate counts)
  const fetchTabCounts = useCallback(async () => {
    if (!organizationId || !supabase) return;

    const { data, error: countError } = await supabase
      .from("message_patterns")
      .select("classification")
      .eq("organization_id", organizationId);

    if (countError || !data) return;

    const counts: TabCounts = { all: data.length, unclassified: 0, manychat: 0, human_script: 0 };
    data.forEach((p) => {
      const c = p.classification as keyof Omit<TabCounts, "all">;
      if (c in counts) counts[c]++;
    });
    setTabCounts(counts);
  }, [organizationId]);

  // Fetch patterns and counts when expanded
  useEffect(() => {
    if (isExpanded) {
      fetchPatterns();
      fetchTabCounts(); // eslint-disable-line react-hooks/set-state-in-effect -- async fetch, not synchronous setState
    }
  }, [isExpanded, fetchPatterns, fetchTabCounts]);

  // Refetch counts after filter change (to catch optimistic updates)
  useEffect(() => {
    if (isExpanded) fetchPatterns();
  }, [filter, fetchPatterns, isExpanded]);

  // Fetch delay modes for all visible patterns (for table display)
  useEffect(() => {
    if (!isExpanded || patterns.length === 0) return;

    // Fetch delay modes for patterns we don't have yet
    const fetchMissingDelayModes = async () => {
      for (const p of patterns) {
        if (delayModes[p.id] === undefined) {
          const mode = await fetchDelayMode(p.id);
          setDelayModes((prev) => ({ ...prev, [p.id]: mode }));
        }
      }
    };

    fetchMissingDelayModes();
  }, [isExpanded, patterns, delayModes, fetchDelayMode]);

  // Classify a single pattern
  const handleClassify = useCallback(
    async (id: string, classification: MessagePattern["classification"]) => {
      await classifyPattern(id, classification);
      // Update counts optimistically
      setTabCounts((prev) => {
        const oldClass = patterns.find((p) => p.id === id)?.classification || "unclassified";
        return {
          ...prev,
          [oldClass]: Math.max(0, prev[oldClass as keyof TabCounts] - 1),
          [classification]: prev[classification as keyof TabCounts] + 1,
        };
      });
    },
    [classifyPattern, patterns]
  );

  // Toggle expanded row (for full message view) and fetch delay mode
  const handleExpandRow = useCallback(
    async (patternId: string) => {
      if (expandedRow === patternId) {
        setExpandedRow(null);
        return;
      }

      setExpandedRow(patternId);

      // Fetch delay mode if not already cached
      if (!delayModes[patternId] && delayModes[patternId] !== null) {
        setLoadingDelayMode(patternId);
        const mode = await fetchDelayMode(patternId);
        setDelayModes((prev) => ({ ...prev, [patternId]: mode }));
        setLoadingDelayMode(null);
      }
    },
    [expandedRow, delayModes, fetchDelayMode]
  );

  // Open sample modal for a pattern
  const handleViewSamples = useCallback((pattern: MessagePattern) => {
    setSampleModalPattern(pattern);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isExpanded || patterns.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const currentPattern = patterns[selectedIndex];
      if (!currentPattern) return;

      switch (e.key.toLowerCase()) {
        case "arrowdown":
        case "j":
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, patterns.length - 1));
          break;
        case "arrowup":
        case "k":
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case "b": // Bot (ManyChat)
          e.preventDefault();
          handleClassify(currentPattern.id, "manychat");
          break;
        case "h": // Human
          e.preventDefault();
          handleClassify(currentPattern.id, "human_script");
          break;
        case "u": // Undo (back to unclassified)
          e.preventDefault();
          handleClassify(currentPattern.id, "unclassified");
          break;
        case "enter":
        case " ":
          e.preventDefault();
          handleExpandRow(currentPattern.id);
          break;
        case "v": // View samples in modal
          e.preventDefault();
          handleViewSamples(currentPattern);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isExpanded,
    patterns,
    selectedIndex,
    expandedRow,
    handleClassify,
    handleExpandRow,
    handleViewSamples,
  ]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0); // eslint-disable-line react-hooks/set-state-in-effect -- intentional reset on filter change
    setExpandedRow(null);
  }, [filter]);

  const handleBulkClassify = async (classification: "manychat" | "human_script") => {
    const unclassified = patterns.filter((p) => p.classification === "unclassified");
    for (const p of unclassified) {
      await classifyPattern(p.id, classification);
    }
    fetchTabCounts();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    });
  };

  const truncate = (text: string, maxLen = 80) =>
    text.length > maxLen ? text.slice(0, maxLen) + "..." : text;

  const tabs: { key: keyof TabCounts; label: string; color: string }[] = [
    { key: "all", label: "All", color: "blue" },
    { key: "unclassified", label: "Unclassified", color: "amber" },
    { key: "manychat", label: "Bot", color: "orange" },
    { key: "human_script", label: "Human", color: "green" },
  ];

  return (
    <div className="liquid-glass anim-lift border border-black/[0.12] rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-ds-amber-bg hover:bg-ds-amber-bg/70 transition-colors border-b border-black/[0.12]"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-5 h-5 text-ds-amber transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-semibold text-glass">Message Pattern Classification</span>
          {tabCounts.unclassified > 0 && (
            <span className="px-2.5 py-1 token-11 font-bold rounded-full bg-amber-500 text-glass animate-pulse">
              {tabCounts.unclassified} to review
            </span>
          )}
        </div>
        {isExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              refreshPatterns().then(() => fetchTabCounts());
            }}
            disabled={isRefreshing}
            className="anim-tap px-4 py-1.5 token-14 font-medium bg-orange-600 text-glass rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-wait transition-colors"
          >
            {isRefreshing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Scanning...
              </span>
            ) : (
              "Scan for Patterns"
            )}
          </button>
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Explanation Banner */}
          <div className="mb-4 p-3 bg-ds-accent/10 border border-ds-accent/30 rounded-lg token-14 text-ds-accent">
            <p className="font-medium mb-1">Why classify patterns?</p>
            <p className="text-ds-accent/80">
              Repeated first messages from setters are detected here. Mark them as{" "}
              <span className="font-semibold text-ds-amber">Bot</span> (ManyChat automation) or{" "}
              <span className="font-semibold text-ds-green">Human</span> (real setter scripts) to
              improve DM analytics accuracy. Bot messages are excluded from setter metrics.
            </p>
          </div>

          {/* Filter Tabs with Counts */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className={`anim-tap px-3 py-1.5 token-14 font-medium rounded-lg transition-all flex items-center gap-2 ${
                    filter === t.key
                      ? `bg-${t.color}-600 text-glass shadow-sm`
                      : `bg-black/[0.05] text-glass-secondary hover:bg-black/[0.08]`
                  }`}
                >
                  {t.label}
                  <span
                    className={`px-1.5 py-0.5 token-11 rounded-full ${
                      filter === t.key
                        ? "bg-black/[0.08] text-glass"
                        : t.color === "blue"
                          ? "bg-ds-accent/15 text-ds-accent"
                          : t.color === "amber"
                            ? "bg-ds-amber/15 text-ds-amber"
                            : t.color === "orange"
                              ? "bg-ds-amber-bg text-ds-amber"
                              : "bg-ds-green-bg text-ds-green"
                    }`}
                  >
                    {tabCounts[t.key]}
                  </span>
                </button>
              ))}
            </div>

            {/* Bulk Actions */}
            {filter === "unclassified" && tabCounts.unclassified > 1 && (
              <button
                onClick={() => handleBulkClassify("manychat")}
                className="anim-tap px-3 py-1.5 token-11 bg-ds-amber-bg text-ds-amber rounded-lg hover:bg-ds-amber-bg/70 transition-colors"
              >
                Mark all {tabCounts.unclassified} as Bot
              </button>
            )}
          </div>

          {/* Keyboard Shortcuts */}
          <div className="mb-3 flex flex-wrap gap-4 token-11 text-glass-muted">
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                ↑↓
              </kbd>{" "}
              Navigate
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                B
              </kbd>{" "}
              Bot
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                H
              </kbd>{" "}
              Human
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                U
              </kbd>{" "}
              Undo
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                Enter
              </kbd>{" "}
              Expand
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-black/[0.05] rounded border border-black/[0.12] text-glass-secondary">
                V
              </kbd>{" "}
              View Samples
            </span>
          </div>

          {/* Loading/Error */}
          {isLoading && (
            <div className="text-center py-12">
              <svg className="animate-spin h-8 w-8 mx-auto text-ds-amber mb-3" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <p className="text-glass-muted">Loading patterns...</p>
            </div>
          )}
          {error && (
            <div className="text-center py-4 text-ds-red bg-ds-red/10 rounded-lg border border-ds-red/30">
              {error}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && patterns.length === 0 && (
            <div className="text-center py-12 bg-black/[0.03] rounded-lg border-2 border-dashed border-black/[0.12]">
              <svg
                className="mx-auto h-12 w-12 text-glass-muted mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-glass-secondary font-medium mb-2">
                {filter === "all"
                  ? "No patterns detected yet"
                  : `No ${filter === "human_script" ? "human" : filter} patterns`}
              </p>
              <p className="text-glass-muted token-14 max-w-md mx-auto">
                {filter === "all"
                  ? 'Click "Scan for Patterns" to detect repeated setter opening messages. Patterns appearing 5+ times will show here.'
                  : filter === "unclassified"
                    ? "All patterns have been classified! Great job."
                    : `No patterns marked as ${filter === "human_script" ? "human scripts" : "bot messages"} yet.`}
              </p>
            </div>
          )}

          {/* Pattern List */}
          {!isLoading && patterns.length > 0 && (
            <div className="border border-black/[0.12] rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-black/[0.03] border-b border-black/[0.12]">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-glass-secondary">
                      Opening Message
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-glass-secondary w-36">
                      Delay Mode
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-glass-secondary w-28">
                      First Seen
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-glass-secondary w-32">
                      Status
                    </th>
                    <th className="px-3 py-3 text-center font-semibold text-glass-secondary w-40">
                      Classify
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {patterns.map((p, idx) => (
                    <React.Fragment key={p.id}>
                      <tr
                        className={`cursor-pointer transition-colors anim-pop ${
                          idx === selectedIndex
                            ? "bg-blue-500/15 border-l-4 border-l-blue-400"
                            : "hover:bg-black/[0.03]"
                        }`}
                        style={{
                          animationDelay: `${Math.min(idx, 15) * 100}ms`,
                          animationDuration: "480ms",
                        }}
                        onClick={() => {
                          setSelectedIndex(idx);
                          handleExpandRow(p.id);
                        }}
                      >
                        <td className="px-4 py-3">
                          <span className="text-glass font-mono token-11 leading-relaxed">
                            {truncate(p.message_text)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {delayModes[p.id] === undefined ? (
                            <span className="text-glass-muted token-11">Loading...</span>
                          ) : delayModes[p.id] ? (
                            <div
                              className={`inline-flex flex-col items-center px-2 py-1 rounded-lg text-xs ${
                                delayModes[p.id]!.confidence >= 0.8
                                  ? "bg-ds-amber/20 text-ds-amber"
                                  : delayModes[p.id]!.confidence >= 0.6
                                    ? "bg-ds-amber/15 text-ds-amber"
                                    : "bg-black/[0.05] text-glass-secondary"
                              }`}
                            >
                              <span className="font-bold">
                                {formatDelayMinutes(delayModes[p.id]!.mode_minutes)}
                              </span>
                              <span className="token-10 opacity-75">
                                {delayModes[p.id]!.mode_count}/{delayModes[p.id]!.total_count}
                              </span>
                            </div>
                          ) : (
                            <span className="text-glass-muted token-11">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center text-glass-muted">
                          {formatDate(p.first_seen_at)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {p.classification === "manychat" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 token-11 rounded-full bg-ds-amber/20 text-ds-amber">
                              Bot
                            </span>
                          ) : p.classification === "human_script" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 token-11 rounded-full bg-ds-green/20 text-ds-green">
                              Human
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 token-11 rounded-full bg-black/[0.05] text-glass-secondary">
                              Unknown
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div
                            className="flex gap-2 justify-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => handleClassify(p.id, "manychat")}
                              className={`anim-tap px-3 py-1.5 token-11 rounded-lg transition-all ${
                                p.classification === "manychat"
                                  ? "bg-orange-500 text-glass shadow-sm ring-2 ring-orange-300"
                                  : "bg-ds-amber-bg text-ds-amber hover:bg-ds-amber-bg/70 border border-ds-amber/30"
                              }`}
                            >
                              Bot
                            </button>
                            <button
                              onClick={() => handleClassify(p.id, "human_script")}
                              className={`anim-tap px-3 py-1.5 token-11 rounded-lg transition-all ${
                                p.classification === "human_script"
                                  ? "bg-green-500 text-glass shadow-sm ring-2 ring-green-300"
                                  : "bg-ds-green-bg text-ds-green hover:bg-ds-green/20 border border-ds-green/30"
                              }`}
                            >
                              Human
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Row */}
                      {expandedRow === p.id && (
                        <tr className="bg-blue-500/5">
                          <td colSpan={5} className="px-6 py-4">
                            <div className="space-y-4">
                              {/* Full Message */}
                              <div>
                                <p className="token-11 font-semibold text-glass-muted uppercase tracking-wide mb-1">
                                  Full Message
                                </p>
                                <div className="bg-black/[0.03] p-3 rounded-lg border border-black/[0.12] font-mono token-13 text-glass whitespace-pre-wrap">
                                  {p.message_text}
                                </div>
                              </div>

                              {/* Metadata */}
                              <div className="flex items-center gap-6 token-11 text-glass-muted">
                                <span>
                                  <span className="font-medium text-glass-secondary">
                                    First seen:
                                  </span>{" "}
                                  {new Date(p.first_seen_at).toLocaleDateString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                    timeZone: timezone,
                                  })}
                                </span>
                                <span>
                                  <span className="font-medium text-glass-secondary">
                                    Last seen:
                                  </span>{" "}
                                  {new Date(p.last_seen_at).toLocaleDateString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                    timeZone: timezone,
                                  })}
                                </span>
                              </div>

                              {/* Delay Mode - helps identify ManyChat automation */}
                              <div className="bg-black/[0.03] p-3 rounded-lg border border-black/[0.12]">
                                <p className="token-11 font-semibold text-glass-muted uppercase tracking-wide mb-2">
                                  Delay Mode Analysis
                                </p>
                                {loadingDelayMode === p.id ? (
                                  <div className="flex items-center gap-2 text-glass-muted text-sm">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                      />
                                    </svg>
                                    Analyzing delays...
                                  </div>
                                ) : delayModes[p.id] ? (
                                  <div className="flex items-center gap-4">
                                    <div
                                      className={`px-3 py-2 rounded-lg ${
                                        delayModes[p.id]!.confidence >= 0.8
                                          ? "bg-ds-amber/20 border border-ds-amber/30"
                                          : delayModes[p.id]!.confidence >= 0.6
                                            ? "bg-ds-amber/15 border border-ds-amber/30"
                                            : "bg-black/[0.03] border border-black/[0.12]"
                                      }`}
                                    >
                                      <p className="token-15 text-glass">
                                        {formatDelayMinutes(delayModes[p.id]!.mode_minutes)}
                                      </p>
                                      <p className="token-11 text-glass-muted">
                                        {delayModes[p.id]!.mode_count} of{" "}
                                        {delayModes[p.id]!.total_count} (
                                        {Math.round(delayModes[p.id]!.confidence * 100)}%)
                                      </p>
                                    </div>
                                    {delayModes[p.id]!.confidence >= 0.8 && (
                                      <div className="flex items-center gap-1 text-ds-amber token-14 font-medium">
                                        <span>
                                          High bot likelihood — same delay{" "}
                                          {delayModes[p.id]!.mode_count}x in a row
                                        </span>
                                      </div>
                                    )}
                                    {delayModes[p.id]!.confidence >= 0.6 &&
                                      delayModes[p.id]!.confidence < 0.8 && (
                                        <div className="flex items-center gap-1 text-ds-amber token-14">
                                          <span>Possible automation</span>
                                        </div>
                                      )}
                                    {delayModes[p.id]!.confidence < 0.6 && (
                                      <div className="flex items-center gap-1 text-ds-green token-14">
                                        <span>Varied timing (likely human)</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="token-14 text-glass-muted">
                                    No delay data available (may be first message in conversation)
                                  </p>
                                )}
                                <p className="token-11 text-glass-muted mt-2">
                                  Mode = most common delay from lead's first message to this pattern
                                  message. ManyChat sends at fixed intervals (30min, 1hr, etc.).
                                </p>
                              </div>

                              {/* View Samples Button */}
                              <div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewSamples(p);
                                  }}
                                  className="anim-tap inline-flex items-center gap-2 px-4 py-2 token-14 font-medium bg-ds-amber/20 text-ds-amber rounded-lg hover:bg-ds-amber/30 transition-colors border border-ds-amber/30"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                  View {Math.min(12, p.occurrence_count)} Sample Conversations
                                </button>
                                <p className="token-11 text-glass-muted mt-1">
                                  Opens 3-column view with the message highlighted in each
                                  conversation
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pattern Sample Modal */}
      <PatternSampleModal
        isOpen={!!sampleModalPattern}
        onClose={() => setSampleModalPattern(null)}
        patternId={sampleModalPattern?.id || null}
        patternText={sampleModalPattern?.message_text || ""}
        occurrenceCount={sampleModalPattern?.occurrence_count || 0}
        organizationId={organizationId}
        timezone={timezone}
        onConversationClick={
          onConversationClick ||
          (() => {
            // Default no-op if not provided
          })
        }
      />
    </div>
  );
};
